import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { combineLatest, concat, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  flatMap,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  BrowserManagerStore,
  SetCookies,
  setCookies,
} from '../../../app/store';
import {
  AccountActionsEnum,
  CancelChangeEmailToken,
  ChangeEmailToken,
  changeEmailTokenError,
  changeEmailTokenSuccess,
  FetchReferer,
  fetchRefererError,
  FetchRefererError,
  fetchRefererSuccess,
  FetchRefererSuccess,
  LoginAccountByApple,
  LoginAccountByChallenge,
  loginAccountByChallengeError,
  LoginAccountByChallengeError,
  LoginAccountByCompany,
  loginAccountByCompany,
  LoginAccountByEmail,
  LoginAccountByFacebook,
  LoginAccountByGoogle,
  LoginAccountError,
  loginAccountError,
  LoginAccountSuccess,
  loginAccountSuccess,
  loginSelectAccount,
  LoginSelectAccount,
  PromptUnusualActivity,
  promptUnusualActivity,
  RegisterAccountByApple,
  RegisterAccountByEmail,
  RegisterAccountByFacebook,
  RegisterAccountByGoogle,
  registerAccountError,
  RegisterAccountError,
  registerAccountSuccess,
  RegisterAccountSuccess,
  ResendUnusualLoginEmail,
  ResendUnusualLoginEmailError,
  resendUnusualLoginEmailError,
  ResendUnusualLoginEmailSuccess,
  resendUnusualLoginEmailSuccess,
  SetPasswordByEmail,
  setPasswordError,
  setPasswordSuccess,
  SetPasswordToken,
  setPasswordTokenError,
  setPasswordTokenSuccess,
  UpdatePasswordToken,
  updatePasswordTokenSuccess,
  VerifyUnusualLogin,
  VerifyUnusualLoginError,
  verifyUnusualLoginError,
  verifyUnusualLoginSuccess,
  VerifyUnusualLoginSuccess,
} from '../actions';
import { AccountBuilder, AccountService } from '../services';
import {
  Account,
  LoginAttemptResult,
  LoginResult,
  Referer,
  SetToken,
  UserAccount,
} from '../models';
import { environment } from '../../../environments';
import { AccountStore } from '../stores';
import { TranslateService } from '@ngx-translate/core';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import {
  ConversionTypeEnum,
  KenshooService,
  NavigationService,
  URLS,
  WindowRefService,
} from 'i2g-ng-utils';
import { ToastService } from 'i2g-ng-ui';

@Injectable()
export class AccountEffects {
  constructor(
    private _actions$: Actions,
    private _accountService: AccountService,
    private _accountStore: AccountStore,
    private _accountBuilder: AccountBuilder,
    private _browserManagerStore: BrowserManagerStore,
    private _windowRefService: WindowRefService,
    private _translateService: TranslateService,
    private _kenshooService: KenshooService,
    private _navigationService: NavigationService,
    private _toastService: ToastService
  ) {}

  @Effect()
  fetchReferer(): Observable<FetchRefererSuccess | FetchRefererError> {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.FETCH_REFERER),
      switchMap(({ payload }: FetchReferer) => {
        return this._accountService
          .getReferringCompany(payload.referralCode)
          .pipe(
            map((referer: Referer) => fetchRefererSuccess(referer)),
            catchError((error) => of(fetchRefererError(error)))
          );
      })
    );
  }

  @Effect()
  registerAccountByEmail(): Observable<
    RegisterAccountSuccess | RegisterAccountError | SetCookies
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_EMAIL),
      flatMap(({ payload }: RegisterAccountByEmail) =>
        this._accountStore.getReferralCode().pipe(
          take(1),
          flatMap((referralCode) =>
            this._accountService.registerAccountByEmail(
              payload.email,
              payload.password,
              payload.locale,
              referralCode
            )
          ),
          this._registerAccountSuccessEffect(payload.trackingScreenName),
          catchError((error) =>
            of(registerAccountError(error, payload.trackingScreenName))
          )
        )
      )
    );
  }

  @Effect()
  registerAccountByGoogle(): Observable<
    RegisterAccountSuccess | RegisterAccountError | SetCookies
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_GOOGLE),
      flatMap(({ payload }: RegisterAccountByGoogle) =>
        this._accountStore.getReferralCode().pipe(
          take(1),
          flatMap((referralCode) =>
            this._accountService.registerAccountByGoogle(
              payload.email,
              payload.idToken,
              payload.locale,
              referralCode
            )
          ),
          this._registerAccountSuccessEffect(payload.trackingScreenName),
          catchError((error) =>
            of(registerAccountError(error, payload.trackingScreenName))
          )
        )
      )
    );
  }

  @Effect()
  registerAccountByFacebook(): Observable<
    RegisterAccountSuccess | RegisterAccountError | SetCookies
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_FACEBOOK),
      flatMap(({ payload }: RegisterAccountByFacebook) =>
        this._accountStore.getReferralCode().pipe(
          flatMap((referralCode) =>
            this._accountService.registerAccountByFacebook(
              payload.email,
              payload.idToken,
              payload.locale,
              referralCode
            )
          ),
          this._registerAccountSuccessEffect(payload.trackingScreenName),
          catchError((error) =>
            of(registerAccountError(error, payload.trackingScreenName))
          )
        )
      )
    );
  }

  @Effect()
  registerAccountByApple(): Observable<
    RegisterAccountSuccess | RegisterAccountError | SetCookies
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_APPLE),
      flatMap(({ payload }: RegisterAccountByApple) =>
        this._accountStore.getReferralCode().pipe(
          flatMap((referralCode) =>
            this._accountService.registerAccountByApple(
              payload.email,
              payload.idToken,
              payload.locale,
              referralCode
            )
          ),
          this._registerAccountSuccessEffect(payload.trackingScreenName),
          catchError((error) =>
            of(registerAccountError(error, payload.trackingScreenName))
          )
        )
      )
    );
  }

  @Effect({ dispatch: false })
  registerAccountSuccess() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.REGISTER_ACCOUNT_SUCCESS),
      switchMap(() =>
        combineLatest([
          this._browserManagerStore.isWeb(),
          this._accountStore.getReturnUrl(),
        ])
      ),
      take(1),
      tap(([isWeb, returnUrl]: [boolean, string]) => {
        this._kenshooService.trackConversion(ConversionTypeEnum.registration);

        if (returnUrl) {
          return this.redirect(returnUrl);
        }
        if (isWeb) {
          return this.redirect(environment.webAppURL);
        }
        return this.redirect(URLS.APPSTORE);
      })
    );
  }

  @Effect()
  loginAccountByEmail(): Observable<
    | LoginSelectAccount
    | LoginAccountByCompany
    | PromptUnusualActivity
    | LoginAccountError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_EMAIL),
      flatMap(({ payload }: LoginAccountByEmail) => {
        const { email, password, locale, trackingScreenName } = payload;

        return this._accountService
          .loginAccountByEmail(email, password, locale)
          .pipe(
            this._loginHandlerEffect(
              payload.locale,
              payload.trackingScreenName
            ),
            catchError((error) => {
              return of(loginAccountError(error, trackingScreenName));
            })
          );
      })
    );
  }

  @Effect()
  loginAccountByFacebook(): Observable<
    | LoginSelectAccount
    | LoginAccountByCompany
    | PromptUnusualActivity
    | LoginAccountError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_FACEBOOK),
      flatMap(({ payload }: LoginAccountByFacebook) =>
        this._accountService
          .loginAccountByFacebook(
            payload.email,
            payload.idToken,
            payload.locale
          )
          .pipe(
            this._loginHandlerEffect(
              payload.locale,
              payload.trackingScreenName
            ),
            catchError((error) =>
              of(loginAccountError(error, payload.trackingScreenName))
            )
          )
      )
    );
  }

  @Effect()
  loginAccountByGoogle(): Observable<
    | LoginSelectAccount
    | LoginAccountByCompany
    | PromptUnusualActivity
    | LoginAccountError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_GOOGLE),
      flatMap(({ payload }: LoginAccountByGoogle) =>
        this._accountService
          .loginAccountByGoogle(payload.email, payload.idToken, payload.locale)
          .pipe(
            this._loginHandlerEffect(
              payload.locale,
              payload.trackingScreenName
            ),
            catchError((error) =>
              of(loginAccountError(error, payload.trackingScreenName))
            )
          )
      )
    );
  }

  @Effect()
  loginAccountByApple(): Observable<
    | LoginSelectAccount
    | LoginAccountByCompany
    | PromptUnusualActivity
    | LoginAccountError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_APPLE),
      flatMap(({ payload }: LoginAccountByApple) =>
        this._accountService
          .loginAccountByApple(payload.email, payload.idToken, payload.locale)
          .pipe(
            this._loginHandlerEffect(
              payload.locale,
              payload.trackingScreenName
            ),
            catchError((error) =>
              of(loginAccountError(error, payload.trackingScreenName))
            )
          )
      )
    );
  }

  @Effect()
  loginAccountByCompany(): Observable<
    LoginAccountSuccess | LoginAccountError | SetCookies
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_COMPANY),
      flatMap(({ payload }: LoginAccountByCompany) =>
        this._accountService
          .loginAccountByCompany(
            payload.accountId,
            payload.refreshToken,
            payload.locale
          )
          .pipe(
            flatMap((loginResult: LoginResult) => {
              const userAccount: UserAccount = loginResult.userAccount;
              const cookies = this._accountBuilder.buildAccountCookies(
                userAccount.accessToken,
                userAccount.refreshToken
              );

              return concat([
                setCookies(cookies),
                loginAccountSuccess(
                  userAccount,
                  payload.accountId,
                  payload.trackingScreenName
                ),
              ]);
            }),
            catchError((error) =>
              of(loginAccountError(error, payload.trackingScreenName))
            )
          )
      )
    );
  }

  @Effect()
  loginAccountByChallenge(): Observable<
    | LoginSelectAccount
    | LoginAccountByCompany
    | LoginAccountError
    | LoginAccountByChallengeError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_BY_CHALLENGE),
      exhaustMap(({ payload }: LoginAccountByChallenge) => {
        const { challenge, locale, trackingScreenName } = payload;

        return this._accountService
          .loginAccountByChallenge(challenge, locale)
          .pipe(
            map((loginResult: LoginResult) => {
              const userAccount: UserAccount = loginResult.userAccount;

              // if result has challenge id only (user hasn't clicked in the email)
              if (!userAccount) {
                return null;
              }

              return userAccount.hasMultipleAccounts
                ? loginSelectAccount(userAccount)
                : loginAccountByCompany(
                    userAccount.accounts.get(0).accountId,
                    userAccount.refreshToken,
                    locale,
                    trackingScreenName
                  );
            }),
            filter((x) => !!x), // TODO: can it be removed? got compiling issue in vscode
            catchError((error) =>
              concat([
                loginAccountError(error, trackingScreenName),
                loginAccountByChallengeError(),
              ])
            )
          );
      })
    );
  }

  @Effect({ dispatch: false })
  loginAccountByChallengeError() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_BY_CHALLENGE_ERROR),
      tap(() => this._navigationService.goTo(['/login']))
    );
  }

  @Effect({ dispatch: false })
  loginAccountSuccess() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_ACCOUNT_SUCCESS),
      switchMap(() => {
        return combineLatest([
          this._browserManagerStore.isWeb(),
          this._accountStore.getReturnUrl(),
        ]).pipe(
          take(1),
          tap(([isWeb, returnUrl]: [boolean, string]) => {
            if (returnUrl) {
              this.redirect(returnUrl);
            } else if (isWeb) {
              this.redirect(environment.webAppURL);
            } else {
              this.redirect(URLS.APPSTORE);
            }
          })
        );
      })
    );
  }

  @Effect({ dispatch: false })
  loginSelectAccount() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.LOGIN_SELECT_ACCOUNT),
      tap(() => this._navigationService.goTo(['accounts']))
    );
  }

  @Effect()
  setPasswordByEmail() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.SET_PASSWORD_EMAIL),
      flatMap(({ payload }: SetPasswordByEmail) =>
        this._accountService
          .setPasswordByEmail(payload.email, payload.locale)
          .pipe(
            map(() => {
              const successMessage = this._translateService.instant(
                'SET_PASSWORD.SET_REQUEST_SUCCESS',
                {
                  email: payload.email,
                  supportUrl: URLS.SUPPORT,
                }
              );
              return setPasswordSuccess(successMessage);
            }),
            catchError((error) => of(setPasswordError(error)))
          )
      )
    );
  }

  @Effect()
  setPasswordToken() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.SET_PASSWORD_TOKEN),
      flatMap(({ payload }: SetPasswordToken) =>
        this._accountService
          .validateSetPasswordToken(payload.token, payload.locale)
          .pipe(
            map((setToken: SetToken) =>
              setPasswordTokenSuccess(
                payload.token,
                setToken.email,
                setToken.userId
              )
            ),
            catchError((error) => of(setPasswordTokenError(error)))
          )
      )
    );
  }

  @Effect({ dispatch: false })
  setPasswordTokenError() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.SET_PASSWORD_TOKEN_ERROR),
      tap(() => this._navigationService.goTo(['set-password', 'invalid']))
    );
  }

  @Effect()
  updatePasswordToken() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.UPDATE_PASSWORD_TOKEN),
      flatMap(({ payload }: UpdatePasswordToken) =>
        this._accountService
          .updatePasswordByToken(
            payload.userId,
            payload.token,
            payload.password,
            payload.locale
          )
          .pipe(
            map(() => updatePasswordTokenSuccess()),
            catchError((error) => of(setPasswordError(error)))
          )
      )
    );
  }

  @Effect({ dispatch: false })
  updatePasswordTokenSuccess() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.UPDATE_PASSWORD_TOKEN_SUCCESS),
      tap(() => this._navigationService.goTo(['set-password', 'success']))
    );
  }

  @Effect()
  changeEmailToken() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.CHANGE_EMAIL_TOKEN),
      flatMap(({ payload }: ChangeEmailToken) =>
        this._accountService
          .verifyEmailByToken(payload.token, payload.locale)
          .pipe(
            map(() => changeEmailTokenSuccess(payload.token)),
            catchError((error) => of(changeEmailTokenError(error)))
          )
      )
    );
  }

  @Effect()
  cancelChangeEmailToken() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.CANCEL_CHANGE_EMAIL_TOKEN),
      flatMap(({ payload }: CancelChangeEmailToken) =>
        this._accountService
          .cancelChangeEmailByToken(payload.token, payload.locale)
          .pipe(
            map(() => changeEmailTokenSuccess(payload.token)),
            catchError((error) => of(changeEmailTokenError(error)))
          )
      )
    );
  }

  @Effect({ dispatch: false })
  changeEmailTokenError() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.CHANGE_EMAIL_TOKEN_ERROR),
      tap(() => this._navigationService.goTo(['change-email', 'invalid']))
    );
  }

  @Effect({ dispatch: false })
  promptUnusualActivity() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.PROMPT_UNUSUAL_ACTIVITY),
      tap(() => this._navigationService.goTo(['unusual-activity']))
    );
  }

  @Effect()
  resendUnusualLoginEmail(): Observable<
    ResendUnusualLoginEmailSuccess | ResendUnusualLoginEmailError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.RESEND_UNUSUAL_LOGIN_EMAIL),
      switchMap(({ payload }: ResendUnusualLoginEmail) => {
        const { attemptId } = payload;

        return this._accountService.resendUnusualLoginEmail(attemptId).pipe(
          map(() => {
            const message = this._translateService.instant(
              'UNUSUAL_ACTIVITY.EMAIL_RESENT'
            );

            this._toastService.show(message);

            return resendUnusualLoginEmailSuccess(message);
          }),
          catchError(() =>
            of(
              resendUnusualLoginEmailError(
                this._translateService.instant(
                  'UNUSUAL_ACTIVITY.ERROR.RESEND_EMAIL'
                )
              )
            )
          )
        );
      })
    );
  }

  @Effect()
  verifyUnusualLogin(): Observable<
    VerifyUnusualLoginSuccess | VerifyUnusualLoginError
  > {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.VERIFY_UNUSUAL_LOGIN),
      switchMap(({ payload }: VerifyUnusualLogin) => {
        const { attemptId, verificationCode } = payload;

        return this._accountService
          .verifyUnusualLogin(attemptId, verificationCode)
          .pipe(
            map((loginAttemptResult: LoginAttemptResult) =>
              verifyUnusualLoginSuccess(loginAttemptResult)
            ),
            catchError(() =>
              of(
                verifyUnusualLoginError(
                  this._translateService.instant('VERIFY_LOGIN.ERROR')
                )
              )
            )
          );
      })
    );
  }

  @Effect({ dispatch: false })
  goToLogin() {
    return this._actions$.pipe(
      ofType(AccountActionsEnum.GO_TO_LOGIN),
      switchMap(() => {
        return this._browserManagerStore
          .isWeb()
          .pipe(
            tap((isWeb: boolean) =>
              isWeb
                ? this._navigationService.goTo(['/login'])
                : this._navigationService.goToExternalUrl(
                    `${environment.redirectServiceUrl}/navigate/home`
                  )
            )
          );
      })
    );
  }

  redirect(url: string) {
    // delay for segment tracking calls to finish
    setTimeout(() => {
      const window = this._windowRefService.nativeWindow;
      window.location.href = url;
    }, 1000);
  }

  private _registerAccountSuccessEffect = (
    trackingScreenName: TrackingScreenNamesEnum
  ) => {
    return flatMap((registeredAccount: Account) => {
      const cookies = this._accountBuilder.buildAccountCookies(
        registeredAccount.accessToken,
        registeredAccount.refreshToken,
        true
      );

      return concat([
        setCookies(cookies),
        registerAccountSuccess(registeredAccount, trackingScreenName),
      ]);
    });
  };

  private _loginHandlerEffect = (
    locale: string,
    trackingScreenName: TrackingScreenNamesEnum
  ) => {
    return map((loginResult: LoginResult) => {
      if (loginResult.challenge) {
        return promptUnusualActivity(loginResult.challenge);
      }

      const userAccount: UserAccount = loginResult.userAccount;

      return userAccount.hasMultipleAccounts
        ? loginSelectAccount(userAccount)
        : loginAccountByCompany(
            userAccount.accounts.get(0).accountId,
            userAccount.refreshToken,
            locale,
            trackingScreenName
          );
    });
  };
}
